// LiveStreamUser.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css, keyframes } from 'styled-components';
import { withTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import UserAvatar from '../container/UserAvatar.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import { color } from '../style/variables.js';

import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';
import ParticleBurst from '../component/ParticleBurst.jsx';
import LiveStreamGiftDiamond from '../component/LiveStreamGiftDiamond.jsx';
import getUserProfileUrl from '../resource/getUserProfileUrl.js';

import BlackAddIconSrc from '../../img/ic-add-black-xs.svg';

import { ButtonId } from '../resource/trackingConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { getIsOnMacOS, getIsOnWindows } from '../resource/getUserAgent.js';

const isOnWindows = getIsOnWindows();
const isOnMacOS = getIsOnMacOS();

// Follow succeed timeline. The JS timer and the CSS keyframes both derive from
// these, so the timing has one source of truth.
const POP_DURATION = 250;
const RIPPLE_DURATION = 500;
const CHECK_DELAY = 150;
const CHECK_DURATION = 200;
const HOLD_DURATION = 400;
const FADE_OUT_DURATION = 350;
const FADE_OUT_DELAY = CHECK_DELAY + CHECK_DURATION + HOLD_DURATION;
const FOLLOW_ANIMATION_DURATION = FADE_OUT_DELAY + FADE_OUT_DURATION;
// Check mark path from img/icon-complete.svg, inlined so it can be drawn stroke
// by stroke. The length is measured off that path — re-measure if it changes.
const CHECK_PATH = 'M6.21497 10.5143L8.83953 13.2058L13.8204 7.05347';
const CHECK_PATH_LENGTH = 12;

export class LiveStreamUser extends React.PureComponent {
  subscriptionReference = `LiveStreamUser-${uuidv4()}`;
  followButtonRef = React.createRef();
  state = { isFollowSucceedAnimating: false };

  componentDidMount() {
    const {
      userId,
      username,
      fetchUser,
      toggleSubscribeOnlineStatus,
      fetchUserIsFollowing,
    } = this.props;
    this.nextTick = setTimeout(() => {
      if (userId && !username) {
        fetchUser({ userId });
      }
      toggleSubscribeOnlineStatus({
        userId,
        isCritical: true,
        shouldSubscribe: true,
        subscriptionReference: this.subscriptionReference,
      });
      fetchUserIsFollowing({ userId });
    });
  }

  componentWillUnmount() {
    const { userId, toggleSubscribeOnlineStatus } = this.props;

    clearTimeout(this.nextTick);
    clearTimeout(this.followAnimationTimeout);
    toggleSubscribeOnlineStatus({
      userId,
      shouldSubscribe: false,
      subscriptionReference: this.subscriptionReference,
    });
  }

  handleToUserProfile = event => {
    const {
      isPrivateShowUnlocked,
      isPrivateStreamUnlocked,
      shouldShowExitAlertModal,
      leaveLiveStreams,
      addModal,
    } = this.props;
    if ((isOnWindows && event.ctrlKey) || (isOnMacOS && event.metaKey)) return;

    if (
      shouldShowExitAlertModal &&
      (isPrivateShowUnlocked || isPrivateStreamUnlocked)
    ) {
      event.preventDefault();
      return addModal({
        id: 'LeaveLivestreamToUserProfileConfirmModal',
      });
    }
    return leaveLiveStreams();
  };

  handleFollow = async () => {
    const { userId, followUser } = this.props;
    if (this.state.isFollowSucceedAnimating) return;

    this.setState({ isFollowSucceedAnimating: true });
    // Awaited alongside the request, not on the timer alone: on a slow network
    // the button would flip back to the plus and then vanish once the response
    // lands. Rejections are swallowed so it cannot stay stuck faded out.
    await Promise.all([
      new Promise(resolve => {
        this.followAnimationTimeout = setTimeout(
          resolve,
          FOLLOW_ANIMATION_DURATION
        );
      }),
      followUser({ userId }),
    ]).catch(() => null);
    this.setState({ isFollowSucceedAnimating: false });
  };

  renderOfflineUser = () => {
    const { t, userId, displayedUsername, username } = this.props;
    return (
      <StyledLiveStreamUserOffline>
        {userId && (
          <UserProfileLink
            to={getUserProfileUrl({ userId, username })}
            onClick={this.handleToUserProfile}
            alt={displayedUsername}
            aria-label={displayedUsername}
          >
            <UserAvatar id={userId} size={72} />
          </UserProfileLink>
        )}
        <OfflineUsername>
          <UserProfileLink
            to={getUserProfileUrl({ userId, username })}
            onClick={this.handleToUserProfile}
            alt={displayedUsername}
            data-element_id={ButtonId.Detail.ContentDetailName}
            data-tracking_payload={{
              'user.id': userId,
              'user.username': username,
            }}
          >
            {displayedUsername}
          </UserProfileLink>
        </OfflineUsername>
        <GoToProfileButton
          to={getUserProfileUrl({ userId, username })}
          data-element_id={ButtonId.LiveStream.ButtonOfflineToProfile}
          data-tracking_payload={{
            'user.id': userId,
          }}
        >
          {t('view_profile', {
            ns: TranslationNamespace.LIVESTREAM,
          })}
        </GoToProfileButton>
      </StyledLiveStreamUserOffline>
    );
  };
  renderOnlineUser = () => {
    const {
      displayedUsername,
      username,
      userId,
      isAuthed,
      isFollowing,
      earnings,
      followUser,
    } = this.props;
    const { isFollowSucceedAnimating } = this.state;
    return (
      <StyledLiveStreamUser isFollowing={isFollowing}>
        {userId && (
          <UserProfileLink
            to={getUserProfileUrl({ userId, username })}
            onClick={this.handleToUserProfile}
            alt={displayedUsername}
            aria-label={displayedUsername}
          >
            <UserAvatar id={userId} size={36} />
          </UserProfileLink>
        )}
        {displayedUsername && (
          <UserInfo>
            <UserProfileLink
              to={getUserProfileUrl({ userId, username })}
              onClick={this.handleToUserProfile}
              alt={displayedUsername}
              data-element_id={ButtonId.Detail.ContentDetailName}
              data-tracking_payload={{
                'user.id': userId,
                'user.username': username,
              }}
            >
              <Username>{displayedUsername}</Username>
            </UserProfileLink>
            <LiveStreamGiftDiamond giftDiamondCount={earnings} />
          </UserInfo>
        )}
        {!isAuthed && (
          <FollowButton
            onClick={() => {
              return followUser({ userId });
            }}
          >
            <img src={BlackAddIconSrc} alt="Follow User" />
          </FollowButton>
        )}
        {isAuthed && (!isFollowing || isFollowSucceedAnimating) && (
          <FollowButton
            ref={this.followButtonRef}
            $isAnimating={isFollowSucceedAnimating}
            data-element_id={ButtonId.All.ButtonFollow}
            data-tracking_payload={{
              'user.id': userId,
            }}
            onClick={this.handleFollow}
          >
            {isFollowSucceedAnimating ? (
              <>
                <PulseRing aria-hidden />
                <CheckMark viewBox="0 0 20 20" role="img" aria-label="Followed">
                  <path d={CHECK_PATH} />
                </CheckMark>
              </>
            ) : (
              <img src={BlackAddIconSrc} alt="Follow User" />
            )}
            <ParticleBurst
              isActive={isFollowSucceedAnimating}
              anchorRef={this.followButtonRef}
            />
          </FollowButton>
        )}
      </StyledLiveStreamUser>
    );
  };
  render() {
    const { offline } = this.props;
    if (offline) return this.renderOfflineUser();
    else return this.renderOnlineUser();
  }
}

LiveStreamUser.propTypes = {
  t: PropTypes.func.isRequired,
  isAuthed: PropTypes.bool,
  earnings: PropTypes.number,
  userId: PropTypes.string,
  username: PropTypes.string,
  displayedUsername: PropTypes.string,
  isFollowing: PropTypes.bool,
  isPrivateShowUnlocked: PropTypes.bool,
  isPrivateStreamUnlocked: PropTypes.bool,
  shouldShowExitAlertModal: PropTypes.bool,
  fetchUser: PropTypes.func,
  followUser: PropTypes.func,
  offline: PropTypes.bool,
  leaveLiveStreams: PropTypes.func,
  fetchUserIsFollowing: PropTypes.func,
  addModal: PropTypes.func,
  toggleSubscribeOnlineStatus: PropTypes.func,
};

LiveStreamUser.defaultProps = {
  earnings: null,
  userId: '',
  username: '',
  displayedUsername: '',
  isFollowing: false,
  isPrivateShowUnlocked: false,
  isPrivateStreamUnlocked: false,
  shouldShowExitAlertModal: false,
  fetchUser: () => null,
  followUser: () => null,
  offline: false,
  leaveLiveStreams: () => null,
  fetchUserIsFollowing: () => null,
  addModal: () => null,
  toggleSubscribeOnlineStatus: () => null,
};

const UserProfileLink = styled(Link)``;

const StyledLiveStreamUser = styled.div`
  max-width: 36.8%;
  max-width: 240px;
  min-width: ${({ isFollowing }) => (isFollowing ? '118px' : '138px')};

  display: inline-flex;
  flex-direction: row;
  align-items: center;
  height: 40px;
  padding: 2px 4px;
  border-radius: 22px;
  background-color: ${color.neutral['10_alpha_40']};
  overflow: hidden;
`;

const UserInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  min-width: 70px;
  margin: 0 4px;
  color: ${color.white};
  text-align: left;
  overflow: hidden;
`;

const Username = styled.h2`
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: ${color.white};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const PopKeyframes = keyframes`
  0% { transform: scale(1); }
  40% { transform: scale(1.15); }
  100% { transform: scale(1); }
`;

const RippleKeyframes = keyframes`
  from { opacity: 0.9; transform: scale(1); }
  to { opacity: 0; transform: scale(1.4); }
`;

const DrawCheckKeyframes = keyframes`
  from { stroke-dashoffset: ${CHECK_PATH_LENGTH}; }
  to { stroke-dashoffset: 0; }
`;

const FadeOutAndShrinkKeyframes = keyframes`
  from { opacity: 1; transform: scale(1); }
  to { opacity: 0; transform: scale(0.6); }
`;

const FollowButton = styled.button`
  ${ResetButtonStyle}
  margin-left: 4px;
  position: relative;
  display: block;
  /* The burst sizes its particles in em off this, which the icon button would
     not otherwise set — inheriting the page size makes them button-sized. */
  font-size: 10px;
  width: 20px;
  height: 20px;
  min-width: 20px;
  outline: 0;
  background-color: ${color.tealBlue};
  border-radius: 50%;
  padding: 4px;
  & > img {
    width: 100%;
    height: 100%;
  }
  ${({ $isAnimating }) =>
    $isAnimating &&
    css`
      pointer-events: none;
      animation:
        ${PopKeyframes} ${POP_DURATION}ms ease-out,
        ${FadeOutAndShrinkKeyframes} ${FADE_OUT_DURATION}ms ease-in
          ${FADE_OUT_DELAY}ms forwards;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    `}
`;

// Halo expanding past the button edge. The ripple must stay within 1.4x, or
// the overflow: hidden on StyledLiveStreamUser clips its right side.
const PulseRing = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 1px solid ${color.tealBlue};
  border-radius: 50%;
  animation: ${RippleKeyframes} ${RIPPLE_DURATION}ms ease-out forwards;

  @media (prefers-reduced-motion: reduce) {
    opacity: 0;
    animation: none;
  }
`;

const CheckMark = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  > path {
    fill: none;
    stroke: ${color.black};
    stroke-width: 1.5;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: ${CHECK_PATH_LENGTH};
    stroke-dashoffset: ${CHECK_PATH_LENGTH};
    animation: ${DrawCheckKeyframes} ${CHECK_DURATION}ms ease-out
      ${CHECK_DELAY}ms forwards;
  }

  @media (prefers-reduced-motion: reduce) {
    > path {
      stroke-dashoffset: 0;
      animation: none;
    }
  }
`;

const StyledLiveStreamUserOffline = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  height: 100%;
`;

const OfflineUsername = styled.div`
  margin-top: 12px;
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: 600;
  color: ${color.white};
`;

const GoToProfileButton = styled(Link)`
  display: block;
  width: 140px;
  height: 24px;
  background-color: ${color.tealBlue};
  border-radius: 24px;
  font-size: 14px;
  color: ${color.white};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default withTranslation()(LiveStreamUser);
